import { useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface ShareableRestaurant {
  id: number | string;
  name: string;
}

/**
 * Shares a restaurant through the Web Share API, falling back to copying the
 * public link to the clipboard. The single share-restaurant implementation:
 * the search card, the map-panel card and the restaurant profile all use it.
 *
 * Emits `restaurant_shared` once per tap. The optional `source` property is
 * only included when a caller passes one (search cards pass 'search_results';
 * the profile passes nothing, so its payload is unchanged).
 *
 * Failure handling: a member cancelling the share sheet (`AbortError`) is
 * silent. Any other native failure (`NotAllowedError` when user activation
 * lapsed, `TypeError`, etc.) falls back to the clipboard, which confirms with a
 * "Link copied!" toast; if the clipboard also fails, a failure toast shows. A
 * second tap while a share is still in flight is ignored, so double-taps
 * cannot fire the event or open two share sheets.
 *
 * The share URL is built from `VITE_PWA_URL` with a hard-coded public
 * fallback — never `window.location`, which on the native app is Capacitor's
 * local WebView origin and is not a shareable URL (GitHub #127).
 *
 * Call the returned function synchronously from the tap handler:
 * `navigator.share` requires transient user activation, so do not `await`
 * anything before invoking it.
 */
export function useRestaurantShare(restaurant: ShareableRestaurant, source?: string) {
  const toast = useToast();
  const inFlight = useRef(false);

  return useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const shareBaseUrl = import.meta.env.VITE_PWA_URL || 'https://app.paire.io';
      const shareUrl = `${shareBaseUrl}/restaurants/${restaurant.id}`;
      const canNativeShare = 'share' in navigator;

      analytics.capture(AnalyticsEvents.RESTAURANT_SHARED, {
        restaurant_id: restaurant.id,
        restaurant_name: restaurant.name,
        share_method: canNativeShare ? 'native' : 'clipboard',
        ...(source ? { source } : {}),
      });

      if (canNativeShare) {
        try {
          await navigator.share({
            title: restaurant.name,
            text: `Check out ${restaurant.name} on Paire!`,
            url: shareUrl,
          });
          return;
        } catch (err) {
          // Member dismissed the share sheet — nothing to do.
          if ((err as { name?: string } | null)?.name === 'AbortError') return;
          // Anything else: fall through to the clipboard path.
        }
      }

      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: 'Link copied!', status: 'success', duration: 2000 });
      } catch {
        toast({ title: 'Could not copy link', status: 'error', duration: 2000 });
      }
    } finally {
      inFlight.current = false;
    }
  }, [restaurant.id, restaurant.name, source, toast]);
}
